import {assert} from 'Asserts';
import * as ast from 'notebook/tables/excess/AST';
import {FullTableMap} from 'notebook/tables/excess/Evaluator';
import {Token} from 'notebook/tables/excess/Tokenizer';
import * as util from 'notebook/tables/excess/Util';
import {
  CellId,
  CellIdWithOptionalTable,
  TABLE_COLUMN,
  TABLE_ROW,
  TableDimension,
} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {assertNever} from 'util/Types';

/**
 * A function that transforms a token.
 *
 * @param token             The token to update
 * @param cellPosition      The position of the cell containing the token
 * @returns                 The new text for the token, or null if the token should not be updated.
 */
export type TokenUpdater = (token: Token, cellPosition: CellId) => string | null;

/**
 * @param targetTableName  The table to delete cells from
 * @param dim              Whether we are deleting rows or columns
 * @param minK             The smaller index dimension
 * @param maxK             The larger index dimension
 * @param tables           The tables in the notebook
 * @return                 Token updater function for deleting elements [minK, maxK] in dimension `dim`
 *                         from table `targetTableName`
 */
export function createDeleteTokenUpdater(
  targetTableName: string,
  dim: TableDimension,
  minK: number,
  maxK: number,
  tables: FullTableMap
): TokenUpdater {
  assert([TABLE_ROW, TABLE_COLUMN].includes(dim), `Unexpected dimension ${dim}`);
  const dimShift = maxK - minK + 1;
  return (token, cellPosition) => {
    if (!token.isCellRef()) {
      return null;
    }

    const {node} = token;
    // Skip tokens that aren't cell references for the target table.
    if ((node.getTable() ?? cellPosition.table) !== targetTableName) {
      return null;
    }

    if (node instanceof ast.Cell) {
      if (dim === TableDimension.ROW) {
        // If a referencing a Single Input, don't shift.
        if (isSingleInputReference(tables, targetTableName, node)) {
          return null;
        }
      }
      // Handle shifting reference for Cell
      if (node.id[dim] < minK) {
        return null;
      }
      if (node.id[dim] <= maxK) {
        return util.ReferenceError.TEXT;
      }

      const newDim = node.id[dim] - dimShift;
      const newId = {table: node.getTable(), row: node.id.row, column: node.id.column};
      newId[dim] = newDim;
      return util.toCellReferenceTokenText(newId, node.locks);
    } else if (node instanceof ast.CellRange) {
      // Handle shifting references for CellRanges
      const {start, end} = node;

      // Sort to simplify processing
      const isSmallerFirst = start[dim] <= end[dim];
      const [smallerId, largerId] = isSmallerFirst ? [start, end] : [end, start];

      // Determine new min row
      let newSmallerDim: number;
      if (smallerId[dim] < minK) {
        newSmallerDim = smallerId[dim];
      } else if (smallerId[dim] <= maxK) {
        // Given the cell range extends from smallerDim->largerDim, when minK <= smallerDim <= maxK, the
        // desired range to keep was originally (maxK + 1)->largerDim. (maxK + 1) maps to minK after
        // applying the shift by dimShift of (maxK - minK + 1).
        newSmallerDim = minK;
      } else {
        newSmallerDim = smallerId[dim] - dimShift;
      }

      // Determine new max row
      let newLargerDim: number;
      if (largerId[dim] < minK) {
        newLargerDim = largerId[dim];
      } else if (largerId[dim] <= maxK) {
        // Given the cell range extends from smallerDim->largerDim, when minK <= largerDim <= maxK, the
        // desired range to keep extends from smallerDim->(minK - 1).
        newLargerDim = minK - 1;
      } else {
        newLargerDim = largerId[dim] - dimShift;
      }

      // If there was no change, continue.
      if (newSmallerDim === smallerId[dim] && newLargerDim === largerId[dim]) {
        return null;
      }
      if (newSmallerDim > newLargerDim) {
        // range is now invalid (e.g. range only containing deleted row)
        return util.ReferenceError.TEXT;
      }

      const newSmallerId = {...smallerId, [dim]: newSmallerDim};
      const newLargerId = {...largerId, [dim]: newLargerDim};

      // Undo the simplifying sort we did earlier
      const [newStartId, newEndId] = isSmallerFirst
        ? [newSmallerId, newLargerId]
        : [newLargerId, newSmallerId];

      return util.toCellRangeReferenceTokenText(newStartId, newEndId, node.locks);
    }
    assertNever(node);
    return null;
  };
}

/**
 * @param targetTableName  The table in which cells were inserted
 * @param dim              Whether we are inserting rows or columns
 * @param insertIndex      The index of the row before which the rows / columns were inserted.
 * @param numNew         The number of rows / columns to insert
 * @return                 TokenUpdater function for inserting `dimShift` rows / columns (along the
 *                         specified `dim`) at the `k`th row / column on table `targetTableName`.
 */
export function createInsertTokenUpdater(
  targetTableName: string,
  dim: TableDimension,
  insertIndex: number,
  numNew: number
): TokenUpdater {
  assert(numNew > 0, 'Expected positive numNew', {numNew});
  const dimShift = numNew; // Rename parameter to match the delete token updater.
  assert([TABLE_ROW, TABLE_COLUMN].includes(dim), `Unexpected dimension ${dim}`);
  return (token, cellPosition) => {
    if (!token.isCellRef()) {
      return null;
    }

    const {node} = token;
    // Skip tokens that aren't cell references for the target table.
    if ((node.getTable() ?? cellPosition.table) !== targetTableName) {
      return null;
    }

    // Handle shifting reference for Cell
    if (node instanceof ast.Cell) {
      // If cell is before the insertion point, it will not move
      if (node.id[dim] < insertIndex) {
        return null;
      }

      // Shift reference by `dimShift` since it moves down `dimShift` rows / columns
      const newDim = node.id[dim] + dimShift;
      const newId = {...node.id, [dim]: newDim};
      return util.toCellReferenceTokenText(newId, node.locks);
    } else if (node instanceof ast.CellRange) {
      // Handle shifting references for CellRanges
      const {start, end} = node;

      // Sort to simplify processing
      const isSmallerFirst = start[dim] <= end[dim];
      const [smallerId, largerId] = isSmallerFirst ? [start, end] : [end, start];

      // Shift the endpoints by `dimShift` if the endpoint is at or after the insertion point `k`
      const newSmallerDim = smallerId[dim] + (smallerId[dim] < insertIndex ? 0 : dimShift);
      const newLargerDim = largerId[dim] + (largerId[dim] < insertIndex ? 0 : dimShift);

      // If there was no change, continue.
      if (newSmallerDim === smallerId[dim] && newLargerDim === largerId[dim]) {
        return null;
      }

      const newSmallerId = {...smallerId, [dim]: newSmallerDim};
      const newLargerId = {...largerId, [dim]: newLargerDim};

      // Undo the simplifying sort we did earlier
      const [newStartId, newEndId] = isSmallerFirst
        ? [newSmallerId, newLargerId]
        : [newLargerId, newSmallerId];

      return util.toCellRangeReferenceTokenText(newStartId, newEndId, node.locks);
    }
    assertNever(node);
    return null;
  };
}

/**
 * This token updater should be used when the rows are sorted.
 *
 * @param targetTableName  The table from which cells were moved
 * @param dim              Dimension that was reordered. Only supports TABLE_ROW
 * @param newToOldIndices  Array mapping from {i -> j} where i is the new row position and j is the old position
 * @return                 Token updater function for applying the given reordering to table `targetTableName`.
 */
export function createSortRowsTokenUpdater(
  targetTableName: string,
  dim: TableDimension,
  newToOldIndices: Array<number>
): TokenUpdater {
  assert([TABLE_ROW].includes(dim), `Unexpected dimention ${dim}. Only supports reordering rows`);

  // Create reverse map for old index -> new index
  const oldToNewIndices: Array<number> = [];
  for (let newIndex = 0; newIndex < newToOldIndices.length; newIndex++) {
    oldToNewIndices[newToOldIndices[newIndex]] = newIndex;
  }

  return (token, cellPosition) => {
    const oldCellPos = {row: cellPosition.row, column: cellPosition.column};
    const newCellPos = {...oldCellPos, [dim]: oldToNewIndices[oldCellPos[dim]]};
    if (
      // Sorting only updates formulas in the table that was modified
      cellPosition.table !== targetTableName ||
      // Sort only updates formulas in moved cells
      newCellPos[dim] === oldCellPos[dim]
    ) {
      return null;
    }

    // Skip tokens that aren't cell references for the target table.
    if (!token.isCellRef()) {
      return null;
    }
    const {node} = token;
    if (
      // Skip tokens that explicitly reference a table (either their own or another table)
      node.getTable() != null
    ) {
      return null;
    }

    if (node instanceof ast.Cell) {
      // Skip updating references that are locked
      if (node.locks[dim === TableDimension.ROW ? 'row' : 'col']) {
        return null;
      }

      const newNode = node.toRelativeNode(oldCellPos).toAbsoluteNode(newCellPos);

      if (newNode instanceof ast.ReferenceErrorNode) {
        return util.ReferenceError.TEXT;
      }

      // Skip updating tokens that have not changed
      if (node.id[dim] === newNode.id[dim]) {
        return null;
      }

      return util.toCellReferenceTokenText(newNode.id, node.locks);
    } else if (node instanceof ast.CellRange) {
      const isStartLocked = node.locks[dim === TableDimension.ROW ? 'startRow' : 'startCol'];
      const isEndLocked = node.locks[dim === TableDimension.ROW ? 'endRow' : 'endCol'];

      // Skip updating ranges that are entirely locked
      if (isStartLocked && isEndLocked) {
        return null;
      }

      const newNode = node.toRelativeNode(oldCellPos).toAbsoluteNode(newCellPos);

      if (newNode instanceof ast.ReferenceErrorNode) {
        return util.ReferenceError.TEXT;
      }

      // Only change the range endpoints that are not locked
      const newStart = (isStartLocked ? node : newNode).start;
      const newEnd = (isEndLocked ? node : newNode).end;

      // Skip updating tokens that have not changed
      if (newStart[dim] === node.start[dim] && newEnd[dim] === node.end[dim]) {
        return null;
      }

      return util.toCellRangeReferenceTokenText(newStart, newEnd, node.locks);
    }
    assertNever(node);
    return null;
  };
}

/**
 * This token updater should be used when the rows or columns are reordered manually.
 *
 * @param targetTableName      The table from which cells were moved
 * @param selectionStartIndex  First index of elements selected to move, inclusive, in original indexing order
 * @param selectionEndIndex    Last index of elements selected to move, inclusive, in original indexing order
 * @param newToOldIndices      Array mapping from {i -> j} where i is the new row/column position and j is the old position
 * @param newToOldIndices      Array mapping from {i -> j} where i is the new row position and j is the old position
 * @return                     Token updater function for applying the given reordering to table `targetTableName`.
 */
export function createReorderTokenUpdater(
  targetTableName: string,
  dim: TableDimension,
  selectionStartIndex: number,
  selectionEndIndex: number,
  newToOldIndices: Array<number>
): TokenUpdater {
  // Create reverse map for old index -> new index
  const _oldToNewIndices: Array<number> = [];
  for (let newIndex = 0; newIndex < newToOldIndices.length; newIndex++) {
    _oldToNewIndices[newToOldIndices[newIndex]] = newIndex;
  }
  function getNewIndex(oldIndex: number): number {
    // When the cell reference is outside the table boundaries, _oldToNewIndices[oldIndex] is undefined,
    // so we fall back to oldIndex. This is correct because reordering will never change the position
    // of cells outside the table boundaries.
    return _oldToNewIndices[oldIndex] ?? oldIndex;
  }

  return (token, cellPosition) => {
    if (!token.isCellRef()) {
      return null;
    }
    const {node} = token;
    // Skip tokens that aren't cell references for the target table
    if ((node.getTable() ?? cellPosition.table) !== targetTableName) {
      return null;
    }

    if (node instanceof ast.Cell) {
      const newIndex = getNewIndex(node.id[dim]);
      // Skip updating tokens that have not changed
      if (node.id[dim] === newIndex) {
        return null;
      }

      const newNodeId = {...node.id, [dim]: newIndex};
      return util.toCellReferenceTokenText(newNodeId, node.locks);
    } else if (node instanceof ast.CellRange) {
      let {start: rangeStart, end: rangeEnd} = node;
      if (rangeStart[dim] > rangeEnd[dim]) {
        [rangeStart, rangeEnd] = [rangeEnd, rangeStart];
      }

      // Skip updating ranges that have not changed
      if (getNewIndex(rangeStart[dim]) === rangeStart[dim] && getNewIndex(rangeEnd[dim]) === rangeEnd[dim]) {
        return null;
      }

      const movedRangeStart = {...rangeStart, [dim]: getNewIndex(rangeStart[dim])};
      const movedRangeEnd = {...rangeEnd, [dim]: getNewIndex(rangeEnd[dim])};

      let newRangeStart: CellIdWithOptionalTable;
      let newRangeEnd: CellIdWithOptionalTable;

      const startBelowEnd = movedRangeStart[dim] > movedRangeEnd[dim];
      const startMovedUp = movedRangeStart[dim] < rangeStart[dim];
      const endMovedDown = movedRangeEnd[dim] > rangeEnd[dim];
      const selectionIsSubsetOfRange =
        selectionStartIndex >= rangeStart[dim] && selectionEndIndex <= rangeEnd[dim];
      if (!startBelowEnd && (startMovedUp || endMovedDown || !selectionIsSubsetOfRange)) {
        // Update the range to be the new positions of the original start and end cells
        newRangeStart = movedRangeStart;
        newRangeEnd = movedRangeEnd;
      } else {
        // Update the range to be the subsection of the range that was NOT included in the selection
        const oldStartDim =
          selectionStartIndex <= rangeStart[dim] && selectionEndIndex >= rangeStart[dim] // Does selection include start?
            ? selectionEndIndex + 1 // Then, new start is the first element after selection
            : rangeStart[dim]; // Otherwise, keep the same start
        const oldEndDim =
          selectionStartIndex <= rangeEnd[dim] && selectionEndIndex >= rangeEnd[dim] // Does selection include end?
            ? selectionStartIndex - 1 // Then, new end is the first element before selection
            : rangeEnd[dim]; // Otherwise, keep the same end
        newRangeStart = {...movedRangeStart, [dim]: getNewIndex(oldStartDim)};
        newRangeEnd = {...movedRangeEnd, [dim]: getNewIndex(oldEndDim)};
      }

      // Skip updating tokens that have not changed
      if (newRangeStart[dim] === rangeStart[dim] && newRangeEnd[dim] === rangeEnd[dim]) {
        return null;
      }

      return util.toCellRangeReferenceTokenText(newRangeStart, newRangeEnd, node.locks);
    }
    assertNever(node);
    return null;
  };
}

/**
 * @param targetTableName  The old name of the table being renamed.
 * @param newTableName     The new name of the table being renamed.
 * @return                 Token updater function for renaming table `targetTableName` to `newTableName`.
 */
export function createRenameTableTokenUpdater(targetTableName: string, newTableName: string): TokenUpdater {
  return (token, cellPosition) => {
    if (!token.isCellRef()) {
      return null;
    }

    const {node} = token;
    // Skip tokens that aren't cell references to the target table.
    if (node.getTable() !== targetTableName) {
      return null;
    }

    if (node instanceof ast.Cell) {
      // Handle replacing reference for Cell
      return util.toCellReferenceTokenText(
        {
          ...node.id,
          table: newTableName,
        },
        node.locks
      );
    } else if (node instanceof ast.CellRange) {
      // Handle replacing references for CellRanges
      return util.toCellRangeReferenceTokenText(
        {...node.start, table: newTableName},
        {...node.end, table: newTableName},
        node.locks
      );
    }
    assertNever(node);
    return null;
  };
}

/**
 * Returns true if the referenced cell is a Single Input cell.
 *
 * Keep in sync with benchling/notebook/tables/excess/table_transform_helpers.py#is_single_input_reference
 */
function isSingleInputReference(tables: FullTableMap, targetTableName: string, node: ast.Cell): boolean {
  return (
    node.id.row === 0 &&
    node.id.column < TableUtil.findSingleInputBoundaryIndex(tables[node.getTable() ?? targetTableName])
  );
}
